import { FieldConfig, ProjectStatus, Technology } from "@renewmap/model";
import { BBox } from "geojson";

export interface NetZeroEntityBase {
  id: string;
  entity_type: string;
  Description: string;
}

export type NetZeroEntity =
  | NetZeroProject
  | NetZeroZone
  | NetZeroNetwork
  | NetZeroSupportProgram
  | NetZeroProjectsStatusGrouping
  | NetZeroProjectsInvestmentGrouping
  | NetZeroProjectCluster;

export type NetZeroTechnology = Technology | "Other";

export interface NetZeroProject extends NetZeroEntityBase {
  id: string;
  entity_type: "project";
  project_name: string;
  technology: NetZeroTechnology;
  project_status: ProjectStatus;
  project_size_mw: number;
  storage_size_mwh: number;
  council_area: string;
  point: [number, number];
  has_geom: boolean;
  sources: Record<string, string>;
  [key: string]: unknown;

  // Project details
  Storage: number;
  "Production Method": string;
  "Electrolyser Type": string;
  "Electrolyser Capacity": string[];
  "Hydrogen Production Capacity": string[];
  "Project Use Case(s)": string;
  "Project Component(s)": string;
  "Component Cost(s)": string;
  "Estimated Commercial Operations Date": Date;
  "Proposed Closure": Date;

  // Employment estimates
  "Construction Employment Estimate": number;
  "Operations Employment Estimate": number;
  "Associated Employment Estimate": number;
  "Estimated Public Benefit": number;

  // Funding schemes
  "Capacity Investment Scheme": string;
  "CEFC Funding Amount": number;
  "CEFC Funding Start Date": Date;
  "CEFC Funding Outcome": string;
  "CEFC Funding Maturity Date": Date;
  "CEFC Funding Details": string;
  "NAIF Funding Amount": number;
  "NAIF Funding Start Date": Date;
  "NRF Funding Amount": number;
  "NRF Funding Start Date": Date;
  "Regional Hydrogen Hubs: Funding Amount": number;
  "Regional Hydrogen Hubs: Funding Start Date": Date;

  "FMA Funding Amount": number;
  "FMA: Funding Start Date": Date;
  "CMDP Funding Amount": number;
  "CMDP: Funding Start Date": Date;
  "IPCM Funding Amount": number;
  "IPCM: Funding Start Date": Date;
  "Investor Front Door Participation Date": Date;

  "ARENA: Funding Amount": number;
  "ARENA: Funding Amount Secondary": number;
  "ARENA: Funding Amount (3)": number;
  "ARENA: Funding Amount (4)": number;

  "ARENA: Funding Start Date": Date;
  "ARENA: Program": string;

  "Total Project Cost": number;

  // Project partners and business status
  "Business.gov.au Major Project Status: Project Description": string;
  "Business.gov.au Major Project Status: Granted Date": Date;
  "Business.gov.au Major Project Status: Expires Date": Date;

  Developer: string[];

  // links
  "Net Zero Energy Authority (NZEA) Region": string[];
  "Associated Renewable Energy Zone(s)": string[];
  State: string[];
}

export interface NetZeroNetwork extends NetZeroEntityBase {
  id: string;
  entity_type: "network";
  infrastructure_name: string;
  infrastructure_status: ProjectStatus;
  voltage: number;
  sources: Record<string, string>;
  [key: string]: unknown;

  // Employment estimates
  "Construction Employment Estimate": number;
  "Operations Employment Estimate": number;
  "Associated Employment Estimate": number;
  "Estimated Public Benefit": number;

  // Funding schemes
  "CEFC Funding Amount": number;
  "CEFC Funding Start Date": Date;
  "CEFC Funding Outcome": string;
  "CEFC Funding Maturity Date": Date;
  "CEFC Funding Details": string;
  "ARENA: Funding Amount": number;
  "ARENA: Funding Amount Secondary": number;
  "ARENA: Funding Amount (3)": number;
  "ARENA: Funding Amount (4)": number;

  "ARENA: Funding Start Date": Date;
  "ARENA: Program": string;

  "Total Project Cost": number;

  // links
  "Net Zero Energy Authority (NZEA) Region": string[];
  "Associated Renewable Energy Zone(s)": string[];
  State: string[];
}

export interface NetZeroZone extends NetZeroEntityBase {
  id: string;
  entity_type: "zone";
  zone_name: string;
  type: string;
  point: [number, number];
  bounds: BBox;
  has_geom: boolean;
  sources: Record<string, string>;
  [key: string]: unknown;

  Rank: number;
}

export interface NetZeroProjectsGrouping extends NetZeroEntityBase {
  id: string;
  name: string;
  amountHidden?: boolean;
  fieldNames: string[];
  fieldValues?: string[];
  groupingFieldName?: string;
  /** Grouping-field values to exclude from this group (e.g. projects that belong to a standalone scheme). */
  excludeGroupingValues?: string[];
  sources: Record<string, string>;
  url?: string;
  [key: string]: unknown;
}

export interface NetZeroProjectsStatusGrouping extends NetZeroProjectsGrouping {
  entity_type: "status";
}

export interface NetZeroProjectsInvestmentGrouping
  extends NetZeroProjectsGrouping {
  entity_type: "investment";
  /** Short label used in hover popups and prose lists, e.g. "CEFC", "ARENA". */
  shortLabel: string;
  /** Numeric fields summed for totals/hover snippets. Empty for non-numeric schemes (CIS, IFD). */
  amountFields: string[];
  /** Start-date field for the generic InvestmentAmountCard renderer. */
  startDateField?: string;
  /** Non-numeric participation field, e.g. CIS's string field or IFD's date. */
  presenceField?: string;
  /** list-panel: show the generic "total investment estimated for a range of X projects" note. */
  showGenericCostEstimate?: boolean;
  /** list-panel: bespoke cost copy, e.g. CEFC's leverage-ratio note. */
  costNote?: { text: string; url?: string };
}

export const investmentPrograms: NetZeroProjectsInvestmentGrouping[] = [
  {
    name: "Future Made in Australia Innovation Fund",
    id: "fma",
    entity_type: "investment",
    shortLabel: "FMA",
    amountFields: ["FMA Funding Amount"],
    startDateField: "FMA: Funding Start Date",
    fieldNames: ["FMA Funding Amount"],
    groupingFieldName: "The Future Made in Australia (FMA) Innovation Fund",
    Description:
      "ARENA's Future Made in Australia (FMA) Innovation Fund supports pre-commercial clean energy technologies.",
    url: "https://arena.gov.au/funding/future-made-in-australia-innovation-fund/",
    sources: {},
  },
  {
    name: "Investor Front Door",
    id: "ifd",
    entity_type: "investment",
    shortLabel: "IFD",
    amountHidden: true,
    amountFields: [],
    presenceField: "Investor Front Door Participation Date",
    fieldNames: ["Investor Front Door Participation Date"],
    Description:
      "Fast-tracks government approvals for major projects in critical minerals, clean fuels and zero-emission infrastructure.",
    url: "https://www.minister.industry.gov.au/ministers/king/media-releases/building-our-economic-resilience-through-investor-front-door",
    sources: {},
  },
  {
    name: "Capacity Investment Scheme",
    id: "cis",
    entity_type: "investment",
    shortLabel: "DCCEEW",
    amountHidden: true,
    amountFields: [],
    presenceField: "Capacity Investment Scheme",
    fieldNames: ["Capacity Investment Scheme"],
    groupingFieldName: "Capacity Investment Scheme",
    Description:
      "Government revenue underwriting to accelerate renewable energy and storage investment.",
    url: "https://www.dcceew.gov.au/energy/renewable/capacity-investment-scheme",
    sources: {},
  },
  {
    name: "Australian Renewable Energy Agency",
    id: "arena",
    entity_type: "investment",
    shortLabel: "ARENA",
    amountFields: [
      "ARENA: Funding Amount",
      "ARENA: Funding Amount Secondary",
      "ARENA: Funding Amount (3)",
      "ARENA: Funding Amount (4)",
    ],
    fieldNames: ["ARENA: Funding Amount"],
    groupingFieldName: "ARENA: Program",
    excludeGroupingValues: [
      "The Future Made in Australia (FMA) Innovation Fund",
    ],
    showGenericCostEstimate: true,
    Description:
      "Supports pre-commercial innovation accelerating Australia's transition to net zero.",
    url: "https://arena.gov.au/",
    sources: {},
  },
  {
    name: "Clean Energy Finance Corporation",
    id: "cefc",
    entity_type: "investment",
    shortLabel: "CEFC",
    amountFields: ["CEFC Funding Amount"],
    fieldNames: ["CEFC Funding Amount"],
    costNote: {
      text: "For each $1 of CEFC capital invested during its lifetime, CEFC has leveraged an additional $3.55 in private sector investment",
      url: "https://www.cefc.com.au/annual-report-2025/",
    },
    Description:
      "Australia's specialist climate investor financing clean energy generation, storage and transmission.",
    url: "https://www.cefc.com.au/",
    sources: {},
  },
  {
    name: "Northern Australia Infrastructure Facility",
    id: "naif",
    entity_type: "investment",
    shortLabel: "NAIF",
    amountFields: ["NAIF Funding Amount"],
    startDateField: "NAIF Funding Start Date",
    fieldNames: ["NAIF Funding Amount"],
    showGenericCostEstimate: true,
    Description:
      "Provides concessional loans for infrastructure in northern Australia and Indian Ocean Territories.",
    url: "https://www.naif.gov.au/",
    sources: {},
  },
  {
    name: "National Reconstruction Fund",
    id: "nrf",
    entity_type: "investment",
    shortLabel: "NRF",
    amountFields: ["NRF Funding Amount"],
    startDateField: "NRF Funding Start Date",
    fieldNames: ["NRF Funding Amount"],
    Description:
      "Supports investments in renewables and low emissions technologies to facilitate decarbonisation.",
    url: "https://www.nrf.gov.au/",
    sources: {},
  },
  {
    name: "Regional Hydrogen Hubs Program",
    id: "rhh",
    entity_type: "investment",
    shortLabel: "DCCEEW",
    amountFields: ["Regional Hydrogen Hubs: Funding Amount"],
    startDateField: "Regional Hydrogen Hubs: Funding Start Date",
    fieldNames: ["Regional Hydrogen Hubs: Funding Amount"],
    Description:
      "Supports the development of hydrogen hubs in regional Australia.",
    url: "https://www.dcceew.gov.au/energy/hydrogen/building-regional-hydrogen-hubs",
    sources: {},
  },
  {
    name: "Critical Minerals Development Fund",
    id: "cmdp",
    entity_type: "investment",
    shortLabel: "CMDP",
    amountFields: ["CMDP Funding Amount"],
    startDateField: "CMDP: Funding Start Date",
    fieldNames: ["CMDP Funding Amount"],
    groupingFieldName: "Critical Minerals Development Program",
    Description:
      "Investing across critical minerals projects to accelerate the development of Australia's processing and manufacturing capabilities.",
    url: "https://www.minister.industry.gov.au/ministers/king/media-releases/government-support-critical-minerals",
    sources: {},
  },
  {
    name: "International Partnerships in Critical Minerals Grants",
    id: "ipcm",
    entity_type: "investment",
    shortLabel: "IPCM",
    amountFields: ["IPCM Funding Amount"],
    startDateField: "IPCM: Funding Start Date",
    fieldNames: ["IPCM Funding Amount"],
    Description:
      "Investing to accelerate critical minerals projects with international partners, supporting supply chains.",
    url: "https://business.gov.au/grants-and-programs/international-partnerships-in-critical-minerals",
    sources: {},
  },
];

export const programHasData = (
  program: NetZeroProjectsInvestmentGrouping,
  entity: NetZeroProject | NetZeroNetwork,
): boolean => {
  if (program.amountFields.length) {
    return program.amountFields.some((f) => typeof entity[f] === "number");
  }
  if (program.presenceField) {
    return Boolean(entity[program.presenceField]);
  }
  return false;
};

export const fundingNumberFields: string[] = investmentPrograms.flatMap(
  (p) => p.amountFields,
);

export const hasInvestmentData = (
  entity: NetZeroProject | NetZeroNetwork,
): boolean =>
  investmentPrograms.some((program) => programHasData(program, entity));

export interface NetZeroSupportProgram extends NetZeroEntityBase {
  id: string;
  entity_type: "program";
  support_program_name: string;
  support_program_description: string;
  source_url: string;
  source_label: string;
  sources: Record<string, string>;
  [key: string]: unknown;

  // links
  "Net Zero Energy Authority (NZEA) Region": string[];
  "Associated Renewable Energy Zone(s)": string[];
  State: string[];
}

export interface NetZeroProjectCluster extends NetZeroEntityBase {
  entity_type: "project_cluster";
  cluster_id: number; // unique across query results, not globally unique, use id for globally unique id
  project_count: number;
  project_ids: string[];
}

export interface NetZeroBootstrapData {
  entities: Record<string, NetZeroEntity>;
  fields: FieldConfig[];
}

export const colorByProjectStatus: Partial<Record<ProjectStatus, string>> = {
  Operating: "#34ba67", // green-600 - operating, default
  Commissioning: "#34ba67",
  Development: "#6a93eb",
  Construction: "#9333ea",
  Concept: "#fef28c",
  Decommissioned: "#adadad",
  Abandoned: "#adadad",
};
